import React from 'react';
import { Box, VStack, Text, Badge, Flex, Tooltip, Grid } from '@chakra-ui/react';
import {
  Market,
  MarketStatus,
  MarketOutcome,
  SelectionStatus,
  SelectionOutcome,
  Side,
  Selection
} from '../../types/marketTypes';
import { Event } from '../../types/types';
import { formatDateTimeLongInTz } from '../../utils/timezone';
import { useTimezone } from '../../contexts/TimezoneContext';

interface DexchMarketContentProps {
  market: Market;
  event?: Event;
  compact?: boolean;
}

export const DexchMarketContent: React.FC<DexchMarketContentProps> = ({
  market,
  event,
  compact = false
}) => {
  const [timeZone] = useTimezone();
  // Helper function to get status color
  const getSelectionStatusColor = (status: SelectionStatus) => {
    switch (status) {
      case SelectionStatus.OPEN:
        return 'green';
      case SelectionStatus.SUSPENDED:
        return 'orange';
      case SelectionStatus.CLOSED:
        return 'gray';
      default:
        return 'gray';
    }
  };

  // Check if market is over/under type
  const isOverUnderMarket = () => {
    return market.selections.some(s =>
      s.name.toLowerCase().includes('over') || s.name.toLowerCase().includes('under')
    );
  };

  // Group selections by line number for over/under markets
  const groupSelectionsByLine = () => {
    const grouped: Record<string, { over?: Selection; under?: Selection }> = {};

    market.selections.forEach(selection => {
      const match = selection.name.match(/(Over|Under)\s+([\d.]+)/i);
      if (match) {
        const type = match[1].toLowerCase();
        const line = match[2];

        if (!grouped[line]) {
          grouped[line] = {};
        }

        if (type === 'over') {
          grouped[line].over = selection;
        } else {
          grouped[line].under = selection;
        }
      }
    });

    return Object.entries(grouped).sort(([a], [b]) => parseFloat(a) - parseFloat(b));
  };

  const shouldDimMarket = () => {
    const hasHiddenStatus = market.marketStatus === 'HIDDEN' as MarketStatus;
    const totalPrices = market.selections.reduce((sum, sel) => sum + sel.prices.length, 0);
    const allPricesAreOne = totalPrices > 0 && market.selections.every(sel =>
      sel.prices.length > 0 && sel.prices.every(price => price.price === 1.00)
    );
    const allPricesAreZero = totalPrices > 0 && market.selections.every(sel =>
      sel.prices.length > 0 && sel.prices.every(price => price.price === 0)
    );
    return hasHiddenStatus || totalPrices === 0 || allPricesAreOne || allPricesAreZero;
  };

  return (
    <Box
      bg={market.marketStatus === MarketStatus.CLOSED && market.marketOutcome !== MarketOutcome.VOIDED && market.marketOutcome !== MarketOutcome.FINISHED ? 'gray.50' : 'transparent'}
      p={compact ? 1 : 2}
      borderRadius="md"
      opacity={shouldDimMarket() ? 0.5 : 1}
    >
      <VStack align="stretch" spacing={compact ? 0 : 1}>
        <Flex
          gap={2}
          width="100%"
          justify="space-between"
          align="flex-start"
          mb={1}
        >
          <VStack align="start" spacing={1} flex="1" minW="0">
            <Tooltip label={`ID: ${market.id} | ${market.name}`} placement="top">
              <Text
                fontWeight="bold"
                fontSize={compact ? "xs" : "sm"}
                cursor="help"
              >
                {market.name.replace(/^Innings Runs:\s*/, '')}
              </Text>
            </Tooltip>
            <Flex gap={1} flexWrap="wrap" align="center">
              <Badge colorScheme={market.marketStatus === MarketStatus.OPEN ? 'green' : 'gray'} fontSize="0.65rem">
                {market.marketStatus}
              </Badge>
              {market.marketOutcome && (
                <Badge colorScheme={market.marketOutcome === MarketOutcome.VOIDED ? 'red' : 'blue'} fontSize="0.65rem">
                  {market.marketOutcome}
                </Badge>
              )}
            </Flex>
          </VStack>
        </Flex>

        {/* GR8 IDs Section - HIDDEN FOR DEXCH */}
        {/* Only show closedAt if it exists */}
        {market.closedAt && (
          <Flex gap={2} mt={1} flexWrap="wrap" fontSize="xs" color="gray.600">
            <Flex align="center">
              <Text fontWeight="medium">Closed At:</Text>
              <Text ml={1}>{formatDateTimeLongInTz(market.closedAt, timeZone)}</Text>
            </Flex>
          </Flex>
        )}

        {isOverUnderMarket() ? (
          <VStack align="stretch" spacing={2} mt={2}>
            {groupSelectionsByLine().map(([line, { over, under }]) => (
              <Box key={line} borderWidth="1px" borderRadius="md" p={2}>
                <Text fontSize="sm" fontWeight="bold" mb={2} color="gray.700">
                  Line {line}
                </Text>
                <Grid templateColumns="repeat(2, 1fr)" gap={3}>
                  {over && (
                    <Box>
                      <Flex justify="space-between" align="center" mb={1}>
                        <Tooltip label={`Selection ID: ${over.id}`} placement="top">
                          <Text fontSize="xs" fontWeight="semibold" color="blue.600" cursor="help">
                            Over
                          </Text>
                        </Tooltip>
                        <Flex gap={1}>
                          <Badge
                            colorScheme={getSelectionStatusColor(over.status)}
                            fontSize="0.5rem"
                            variant="solid"
                          >
                            {over.status}
                          </Badge>
                          {over.outcome && (
                            <Badge
                              colorScheme={
                                over.outcome === SelectionOutcome.WIN ? 'green' :
                                  over.outcome === SelectionOutcome.LOSE ? 'red' : 'red'
                              }
                              fontSize="0.5rem"
                            >
                              {over.outcome}
                            </Badge>
                          )}
                        </Flex>
                      </Flex>
                      {over.prices.length > 0 && (
                        <VStack align="stretch" spacing={1}>
                          {over.prices.map((price) => (
                            <Flex key={price.id} gap={1} align="center" fontSize={compact ? "xs" : "sm"}>
                              <Badge
                                variant="outline"
                                colorScheme={price.side === Side.BACK ? 'blue' : 'red'}
                                fontSize="0.6rem"
                              >
                                {price.side}
                              </Badge>
                              <Text fontWeight="semibold">{price.price.toFixed(2)}</Text>
                              {price.probability !== undefined && (
                                <Text color="gray.600" fontSize="xs">
                                  ({(price.probability * 100).toFixed(1)}%)
                                </Text>
                              )}
                            </Flex>
                          ))}
                        </VStack>
                      )}
                    </Box>
                  )}
                  {under && (
                    <Box>
                      <Flex justify="space-between" align="center" mb={1}>
                        <Tooltip label={`Selection ID: ${under.id}`} placement="top">
                          <Text fontSize="xs" fontWeight="semibold" color="red.600" cursor="help">
                            Under
                          </Text>
                        </Tooltip>
                        <Flex gap={1}>
                          <Badge
                            colorScheme={getSelectionStatusColor(under.status)}
                            fontSize="0.5rem"
                            variant="solid"
                          >
                            {under.status}
                          </Badge>
                          {under.outcome && (
                            <Badge
                              colorScheme={
                                under.outcome === SelectionOutcome.WIN ? 'green' :
                                  under.outcome === SelectionOutcome.LOSE ? 'red' : 'red'
                              }
                              fontSize="0.5rem"
                            >
                              {under.outcome}
                            </Badge>
                          )}
                        </Flex>
                      </Flex>
                      {under.prices.length > 0 && (
                        <VStack align="stretch" spacing={1}>
                          {under.prices.map((price) => (
                            <Flex key={price.id} gap={1} align="center" fontSize={compact ? "xs" : "sm"}>
                              <Badge
                                variant="outline"
                                colorScheme={price.side === Side.BACK ? 'blue' : 'red'}
                                fontSize="0.6rem"
                              >
                                {price.side}
                              </Badge>
                              <Text fontWeight="semibold">{price.price.toFixed(2)}</Text>
                              {price.probability !== undefined && (
                                <Text color="gray.600" fontSize="xs">
                                  ({(price.probability * 100).toFixed(1)}%)
                                </Text>
                              )}
                            </Flex>
                          ))}
                        </VStack>
                      )}
                    </Box>
                  )}
                </Grid>
              </Box>
            ))}
          </VStack>
        ) : (
          market.selections.map((selection) => (
            <VStack key={selection.id} align="stretch" spacing={0} mt={compact ? 0 : 1}>
              <Flex
                direction={{ base: compact ? 'row' : 'column', sm: 'row' }}
                justify="space-between"
                align={{ base: 'flex-start', sm: 'center' }}
                gap={{ base: 1, sm: 0 }}
              >
                <Tooltip label={`Selection ID: ${selection.id}`} placement="top">
                  <Text fontSize="xs" fontWeight="semibold" cursor="help">
                    {selection.name}
                  </Text>
                </Tooltip>
                <Flex gap={1} flexWrap="wrap" align="center">
                  <Tooltip label={`Selection Status: ${selection.status}`}>
                    <Badge
                      colorScheme={getSelectionStatusColor(selection.status)}
                      fontSize="0.5rem"
                      variant="solid"
                    >
                      {selection.status}
                    </Badge>
                  </Tooltip>
                  {selection.outcome && (
                    <Badge
                      colorScheme={
                        selection.outcome === SelectionOutcome.WIN
                          ? 'green'
                          : selection.outcome === SelectionOutcome.LOSE
                            ? 'red'
                            : selection.outcome === SelectionOutcome.VOID
                              ? 'red'
                              : 'gray'
                      }
                      fontSize="0.5rem"
                    >
                      {selection.outcome}
                    </Badge>
                  )}
                </Flex>
              </Flex>
              {selection.prices.length > 0 && (
                <Flex
                  flexWrap="wrap"
                  gap={{ base: 2, md: 4 }}
                  pl={compact ? 1 : 2}
                  align="center"
                >
                  {selection.prices.map((price) => (
                    <Flex key={price.id} gap={compact ? 0.5 : 1} align="center" fontSize={compact ? "xs" : "md"} mr={compact ? 1 : 2}>
                      <Badge
                        size="sm"
                        variant="outline"
                        colorScheme={price.side === Side.BACK ? 'blue' : 'red'}
                        fontSize={compact ? "0.6rem" : "0.7rem"}
                      >
                        {price.side}
                      </Badge>
                      <Flex align="center">
                        <Text fontWeight="semibold">{price.price.toFixed(2)}</Text>
                        {price.priceChange === 'up' && (
                          <Box ml={1} color="green.500" fontWeight="bold">
                            <Text as="span" fontSize={compact ? "xs" : "sm"}>▲</Text>
                          </Box>
                        )}
                        {price.priceChange === 'down' && (
                          <Box ml={1} color="red.500" fontWeight="bold">
                            <Text as="span" fontSize={compact ? "xs" : "sm"}>▼</Text>
                          </Box>
                        )}
                      </Flex>
                      {price.probability !== undefined && (
                        <Text color="gray.600" fontSize={compact ? "xs" : "sm"} ml={1}>
                          ({(price.probability * 100).toFixed(1)}%)
                        </Text>
                      )}
                      {price.outcomeValue && (
                        <Text color="gray.500">({price.outcomeValue})</Text>
                      )}
                    </Flex>
                  ))}
                </Flex>
              )}
            </VStack>
          ))
        )}
      </VStack>
    </Box>
  );
};
